// src/services/FinService.js
// Imports for trackEvent
import { getUserData } from '../auth';
import { userApi } from '../auth/api';
import { getWorkspace } from '../workspace/slice.ts';

class FinService {
  constructor() {
    this.isInitialized = false;
    this.userJourney = {
      currentPage: window?.location?.pathname,
      path: [],
      currentAction: null,
      lastUpdated: new Date(),
    };

    // For buffering frequent updates
    this.pendingUpdates = {};
    this.updateTimeout = null;
    this.updateInterval = 500; // ms
  }

  init(organizationId, options = {}) {
    if (this.isInitialized) return Promise.resolve(true);

    // Return a promise that resolves when Fin is initialized
    return new Promise((resolve, reject) => {
      // If Fin is already available, initialize immediately
      if (window.Intercom) {
        this._doInitialization(organizationId, options);
        resolve(true);
      } else {
        // Set up a retry mechanism to check for Fin
        let attempts = 0;
        const maxAttempts = 10;
        const checkInterval = 500; // ms

        const checkForFin = () => {
          attempts++;
          if (window.Intercom) {
            this._doInitialization(organizationId, options);
            const visitorId = window?.Intercom('getVisitorId');
            console.log('visitorId intercom', visitorId);
            const isIntercomBooted = window?.Intercom('booted');
            console.log('isIntercomBooted intercom', isIntercomBooted);

            resolve(true);
          } else if (attempts < maxAttempts) {
            setTimeout(checkForFin, checkInterval);
          } else {
            const error =
              'Fin SDK not found after multiple attempts. Make sure it is loaded.';
            console.error(error);
            resolve(false);
          }
        };

        // Start checking for Fin
        checkForFin();
      }
    });
  }

  // Helper method to perform the actual initialization
  _doInitialization(organizationId, options = {}) {
    window.Intercom('shutdown');
    window.Intercom('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: organizationId,
      ...options,
    });
    //   window.Fin.init({
    //     organizationId,
    //     ...options
    //   });
    // Intercom({

    //   app_id: 'j0l85hdq',

    //   user_id: userData?.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID

    //   name: userData?.firstName + ' ' + userData?.lastName, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name

    //   email: userData?.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email

    //   // created_at: userData?., // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200

    // });
    this.isInitialized = true;
    console.log('FinService initialized');
  }

  // Track page navigation (call this from router)
  trackNavigation({ location }) {}

  // Only update if page actually changed
  // if (currentPath !== this.userJourney.currentPage) {
  //   // Add current page to path history before updating
  //   this.userJourney.path.push(this.userJourney.currentPage);

  trackEvent(event, data = {}) {
    import('../root').then(({ dispatch, store }) => {
      // Get workspace data
      const userData = getUserData(store.getState());
      const workspace = getWorkspace(store.getState());
      const userDataForMixpanel = removeEmptyStrings({
        user_id: userData?.id,
        email: userData?.email,
        name: userData?.firstName,
        fullName:
          userData?.firstName && userData?.lastName
            ? userData?.firstName + ' ' + userData?.lastName
            : '',
        sellerWorkspaceId: userData?.workspaceId,
        phoneNumber: userData?.mobile,
      });

      dispatch(
        userApi.endpoints.trackEvent.initiate({
          eventType: event,
          event: {
            ...data,
            ...userDataForMixpanel,
            ...workspace,
            ...workspace.user,
            ...workspace.businessDetails,
          },
          // workspaceId: workspace?.id || data.workspaceId,
          // workspaceName: workspace?.spaceName
        })
      );
    });

    if (!this.isInitialized || !event) return;
    window.Intercom('trackEvent', event);

    // Use the RTK Query API to track events
  }

  // Track page navigation (call this from router)
  // trackNavigation({ location }) {
  //   if (!this.isInitialized) return;

  //   const currentPath = location.pathname;

  //   // Update current page
  //   this.userJourney.currentPage = currentPath;
  //   this.userJourney.lastUpdated = new Date();

  //   // Update Fin with new navigation info
  //   this.updateFinContext({
  //     pathChanged: true,
  //     newPath: currentPath
  //   });
  // }

  // Track user actions (call this from components)
  trackAction(actionName, details = {}) {
    if (!this.isInitialized) return;

    this.userJourney.currentAction = actionName;
    this.userJourney.lastUpdated = new Date();

    // Update Fin with new action info
    this.updateFinContext({
      userAction: actionName,
      actionDetails: details,
    });
  }

  // Open Fin support widget with current context
  openSupport(additionalContext = {}) {
    if (!this.isInitialized || !window.Intercom) return;

    //   window.Fin.open({
    //     contextData: this.getFullContext(additionalContext)
    //   });
  }

  // Update context for already-open Fin widget
  updateFinContext(newData = {}) {
    if (!this.isInitialized || !window.Intercom) return;
    console.log('updateFinContext', window.Intercom);

    // Buffer updates to avoid too many calls
    Object.entries(newData).forEach(([key, value]) => {
      this.pendingUpdates[key] = value;
    });

    window.Intercom('update', {
      contextData: this.getFullContext({ ...this.pendingUpdates }),
    });
    // If Fin is open, schedule an update
    //   if (window.Intercom.isOpen && window.Intercom.isOpen() && !this.updateTimeout) {
    //     this.updateTimeout = setTimeout(() => {
    //       window.Intercom("update", {
    //         contextData: this.getFullContext(this.pendingUpdates)
    //       });

    //       this.pendingUpdates = {};
    //       this.updateTimeout = null;
    //     }, this.updateInterval);
    //   }
  }

  // Get complete context object for Fin
  getFullContext(additionalContext = {}) {
    return {
      url: window.location.href,
      pathname: window.location.pathname,
      pageTitle: document.title,
      navigationPath:
        this.userJourney.path.join(' → ') +
        ' → ' +
        this.userJourney.currentPage,
      currentAction: this.userJourney.currentAction,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      // Include any component-specific context
      ...additionalContext,
    };
  }

  // Check if Fin is currently open
  isFinOpen() {
    return (
      this.isInitialized &&
      window.Intercom &&
      window.Intercom.isOpen &&
      window.Intercom.isOpen()
    );
  }
}

// Create and export a singleton instance
// const finService = new FinService();
// export { finService };

const removeEmptyStrings = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== '' && value != null)
  );
};

const finService = new FinService();
export { finService };
